import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../services/logger';
import {
  browserKeepsRefusal,
  isDismissalMessage,
  kindsForRequest,
  queryPermissionState,
} from '../lib/mediaPermissionState';
import type { MediaKind, MediaRequest } from '../providers/types';

export type PermissionCheckState = 'checking' | 'granted' | 'prompt' | 'denied' | 'unsupported';

export interface MediaPermissions {
  /** Overall permission state (worst of the devices named by `required`) */
  state: PermissionCheckState;
  /** Individual camera permission state */
  camera: PermissionState | 'unsupported';
  /** Individual microphone permission state */
  microphone: PermissionState | 'unsupported';
  /**
   * Request permissions via getUserMedia (for 'prompt' state).
   * Asks for `required` unless told otherwise — a voice consultation must not
   * open the camera to find out whether it may use the microphone.
   */
  requestPermissions: (request?: MediaRequest) => Promise<boolean>;
  /** Whether the check is still in progress */
  isChecking: boolean;
  /**
   * True when the last request ended with the browser dialog closed rather than
   * answered — the permission still reads `prompt`, and asking again will show
   * the dialog again. Cleared by the next request.
   */
  dismissed: boolean;
  /**
   * Whether a request for these devices would be refused without a dialog.
   *
   * Read synchronously from the live PermissionStatus, so a click handler can
   * decide before calling `getUserMedia` without an `await` that would cost it
   * the user gesture. False wherever the browser cannot be asked, and always
   * false in Safari, which asks again on a click whatever the status says.
   */
  isBlocked: (request?: MediaRequest) => boolean;
}

/**
 * Queries browser permissions for camera and microphone access.
 *
 * Checks permission state non-intrusively using navigator.permissions.query()
 * before triggering the browser dialog, allowing the app to show explanatory
 * UI first. Listens for permission state changes reactively.
 *
 * @param required - The devices this consultation actually needs. It governs both
 *   what `requestPermissions` asks the browser for and what the overall `state`
 *   is derived from: a voice consultation promises the customer their camera
 *   stays off, so it must neither ask for one nor sit on 'prompt' waiting for a
 *   camera answer that will never come. The individual `camera` / `microphone`
 *   states always report what the browser says, whether or not we asked.
 *
 * @returns MediaPermissions object with overall state, individual states,
 *          and requestPermissions function to trigger getUserMedia dialog
 *
 * @example
 * const permissions = useMediaPermissions('microphone');
 *
 * if (permissions.state === 'granted') {
 *   // Proceed with connection
 * } else if (permissions.state === 'prompt') {
 *   // Show explanatory UI, then call permissions.requestPermissions()
 * } else if (permissions.state === 'denied') {
 *   // Show guidance to re-enable in browser settings
 * } else if (permissions.state === 'unsupported') {
 *   // Fall through to connection (Permissions API not available)
 * }
 */
export function useMediaPermissions(required: MediaRequest = 'both'): MediaPermissions {
  const [state, setState] = useState<PermissionCheckState>('checking');
  const [cameraState, setCameraState] = useState<PermissionState | 'unsupported'>('prompt');
  const [microphoneState, setMicrophoneState] = useState<PermissionState | 'unsupported'>('prompt');
  const [isChecking, setIsChecking] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Store permission status objects and handler refs for cleanup
  const cameraStatusRef = useRef<PermissionStatus | null>(null);
  const microphoneStatusRef = useRef<PermissionStatus | null>(null);
  const cameraHandlerRef = useRef<(() => void) | null>(null);
  const microphoneHandlerRef = useRef<(() => void) | null>(null);

  // Derive overall state from the states of the devices this consultation needs.
  // A device nobody asked about cannot hold the flow on 'prompt' — that is how a
  // voice consultation used to wait forever on a camera answer.
  const deriveOverallState = useCallback((
    states: Array<PermissionState | 'unsupported'>
  ): PermissionCheckState => {
    // If any is unsupported, overall is unsupported
    if (states.includes('unsupported')) {
      return 'unsupported';
    }

    // If any is denied, overall is denied
    if (states.includes('denied')) {
      return 'denied';
    }

    // If any is prompt (and none is denied), overall is prompt
    if (states.includes('prompt')) {
      return 'prompt';
    }

    // All required devices granted
    return 'granted';
  }, []);

  const stateOf = useCallback(
    (kind: MediaKind) => (kind === 'camera' ? cameraState : microphoneState),
    [cameraState, microphoneState]
  );

  // Update overall state when individual states change
  useEffect(() => {
    setState(deriveOverallState(kindsForRequest(required).map(stateOf)));
  }, [required, stateOf, deriveOverallState]);

  // Query permissions on mount
  useEffect(() => {
    const queryPermissions = async () => {
      try {
        // Query both permissions in parallel
        const results = await Promise.allSettled([
          navigator.permissions.query({ name: 'camera' as PermissionName }),
          navigator.permissions.query({ name: 'microphone' as PermissionName }),
        ]);

        // Process camera permission
        if (results[0].status === 'fulfilled') {
          const cameraStatus = results[0].value;
          cameraStatusRef.current = cameraStatus;
          setCameraState(cameraStatus.state);

          // Listen for changes (store handler ref for cleanup)
          const onCameraChange = () => setCameraState(cameraStatus.state);
          cameraHandlerRef.current = onCameraChange;
          cameraStatus.addEventListener('change', onCameraChange);
        } else {
          logger.warn('Camera permission query not supported', { error: results[0].reason });
          setCameraState('unsupported');
        }

        // Process microphone permission
        if (results[1].status === 'fulfilled') {
          const microphoneStatus = results[1].value;
          microphoneStatusRef.current = microphoneStatus;
          setMicrophoneState(microphoneStatus.state);

          // Listen for changes (store handler ref for cleanup)
          const onMicrophoneChange = () => setMicrophoneState(microphoneStatus.state);
          microphoneHandlerRef.current = onMicrophoneChange;
          microphoneStatus.addEventListener('change', onMicrophoneChange);
        } else {
          logger.warn('Microphone permission query not supported', { error: results[1].reason });
          setMicrophoneState('unsupported');
        }

        setIsChecking(false);
      } catch (error) {
        logger.error('Failed to query permissions', { error });
        setCameraState('unsupported');
        setMicrophoneState('unsupported');
        setIsChecking(false);
      }
    };

    queryPermissions();

    // Cleanup: remove event listeners on unmount using stored handler refs
    return () => {
      if (cameraStatusRef.current && cameraHandlerRef.current) {
        cameraStatusRef.current.removeEventListener('change', cameraHandlerRef.current);
      }
      if (microphoneStatusRef.current && microphoneHandlerRef.current) {
        microphoneStatusRef.current.removeEventListener('change', microphoneHandlerRef.current);
      }
    };
  }, []);

  // Request permissions via getUserMedia, for the requested devices only.
  // Asking for the camera on a voice consultation is not a harmless extra: the
  // browser dialog names every device asked for, and shows a live preview of the
  // camera the screen has just promised will stay off.
  const requestPermissions = useCallback(async (
    request: MediaRequest = required
  ): Promise<boolean> => {
    const kinds = kindsForRequest(request);
    const wantsCamera = kinds.includes('camera');
    const wantsMicrophone = kinds.includes('microphone');

    setDismissed(false);
    try {
      logger.info('Requesting media permissions', { request });

      const stream = await navigator.mediaDevices.getUserMedia({
        video: wantsCamera,
        audio: wantsMicrophone,
      });

      // Immediately stop all tracks to release camera/microphone
      stream.getTracks().forEach(track => track.stop());

      logger.info('Permissions granted by user', { request });

      if (wantsCamera) setCameraState('granted');
      if (wantsMicrophone) setMicrophoneState('granted');

      return true;
    } catch (error) {
      const { name, message } = (error ?? {}) as { name?: unknown; message?: unknown };
      logger.error('Permission request failed', { error: { name, message }, request });

      // A failure is not a refusal. The dialog closed with a click on the cross
      // leaves the permission at 'prompt'; a busy or missing device leaves it
      // wherever it was. Marking either 'denied' sent the customer to the
      // "Access Required" screen with instructions for a block that did not exist.
      // So the state is read back from the browser — only where it cannot say
      // does a refusal fall back to marking the requested devices denied, and
      // only for a refusal. Never a device this call did not ask about.
      const refused = name === 'NotAllowedError' || name === 'PermissionDeniedError';
      const kindsAsked: MediaKind[] = [
        ...(wantsCamera ? (['camera'] as const) : []),
        ...(wantsMicrophone ? (['microphone'] as const) : []),
      ];
      const readBack = await Promise.all(kindsAsked.map(queryPermissionState));
      kindsAsked.forEach((kind, i) => {
        const setKindState = kind === 'camera' ? setCameraState : setMicrophoneState;
        const actual = readBack[i];
        if (actual !== 'unsupported') setKindState(actual);
        else if (refused) setKindState('denied');
      });

      // Chrome's third closed dialog still says "dismissed" while the block is
      // already on, so a denied read-back overrides the wording.
      const readsDenied = readBack.includes('denied');
      setDismissed(
        refused &&
          !readsDenied &&
          (isDismissalMessage(message) ||
            (readBack.length > 0 && readBack.every((state) => state === 'prompt')))
      );

      return false;
    }
  }, [required]);

  const isBlocked = useCallback((request: MediaRequest = required): boolean => {
    if (!browserKeepsRefusal()) return false;
    return kindsForRequest(request).some((kind) => {
      const status = kind === 'camera' ? cameraStatusRef.current : microphoneStatusRef.current;
      return status?.state === 'denied';
    });
  }, [required]);

  return {
    state,
    camera: cameraState,
    microphone: microphoneState,
    requestPermissions,
    isChecking,
    dismissed,
    isBlocked,
  };
}
